import { BOOK_NAMES, NT_BOOKS, OT_BOOKS } from '../utils/bibleBooks';
import { parseMarkerParts } from '../bible/markerParts';

// ── Anchoring highlights and notes to verse text ─────────────────────────────
// A mark is stored as book + chapter + verse + language + [start, end) character
// offsets into that verse's plain text: the words the reader sees, without the
// verse number and without any "[a]" ref markers. The Reading page and The
// Bible draw the same verse from the same text, so a mark made on one shows on
// the other. Everything here is pure, so it can be tested without a browser.

export const STYLES = ['highlight', 'underline', 'wavy', 'none'];
export const COLORS = ['yellow', 'green', 'blue', 'red', 'purple'];
export const LANGS  = ['en', 'es', 'zh', 'sc'];
export const NOTE_MAX  = 2000;
// The quote is a check, not a copy: long enough to tell the verse still reads
// the same, short enough to keep the free-plan rows small.
export const QUOTE_MAX = 300;
// How far a quote may have drifted and still be found again. Covers spacing
// around ref markers and small corrections; anything further is treated as a
// different text and the mark is skipped rather than drawn on the wrong words.
const DRIFT = 40;

const BOOK_ORDER = Object.fromEntries([...OT_BOOKS, ...NT_BOOKS].map((b, i) => [b, i]));

export const verseKey = (book, chapter, verse, lang) => `${book}|${chapter}|${verse}|${lang}`;

// Canonical order: book, then chapter, then verse.
export function compareVerses(a, b) {
  return ((BOOK_ORDER[a.book] ?? 999) - (BOOK_ORDER[b.book] ?? 999))
    || (a.chapter - b.chapter) || (a.verse - b.verse);
}

export function quoteOf(text, start, end) {
  return text.slice(start, end).slice(0, QUOTE_MAX);
}

// Does text[start, end) still read as the stored quote? A quote cut at
// QUOTE_MAX only has to match as a prefix.
function fits(text, start, end, quote) {
  if (start < 0 || end > text.length || end <= start) return false;
  const seg = text.slice(start, end);
  return quote.length >= QUOTE_MAX ? seg.startsWith(quote) : seg === quote;
}

// Finds `quote` in `text` nearest to `near`, within `drift` characters.
// Returns [start, end] or null.
function locate(text, quote, near, length, drift = DRIFT) {
  if (!quote) return null;
  let best = null;
  for (let i = text.indexOf(quote); i !== -1; i = text.indexOf(quote, i + 1)) {
    if (i + length > text.length) break;
    if (Math.abs(i - near) <= drift && (best === null || Math.abs(i - near) < Math.abs(best - near))) best = i;
  }
  return best === null ? null : [best, best + length];
}

// Where a stored mark falls in `text` now: its own offsets when the quote
// still matches there, otherwise the nearest nearby copy of the quote, or null
// when the verse no longer reads that way.
export function resolveRange(text, rec) {
  const { start, end, quote } = rec;
  if (!text || !quote) return null;
  if (fits(text, start, end, quote)) return [start, end];
  const length = quote.length >= QUOTE_MAX ? end - start : quote.length;
  return locate(text, quote, start, length);
}

// Maps a range from one rendering of a verse to another: the plain text and
// the ref-marked text with its markers taken out. They are normally identical,
// and then this is the identity. When the marked source spaces or punctuates a
// verse slightly differently, the selected words are looked for near the same
// relative position instead.
export function mapRange(fromText, toText, start, end) {
  if (fromText === toText) return [start, end];
  if (!fromText || !toText) return null;
  const quote = fromText.slice(start, end);
  const near  = Math.round(start * toText.length / fromText.length);
  return locate(toText, quote, near, quote.length, Math.max(DRIFT, Math.round(toText.length * 0.1)))
    || mapBySkeleton(fromText, toText, start, end);
}

// The Reading page's poetry splits a verse into lines joined by " / ", while
// the ref-marked text runs them together with a space, so a range crossing a
// line break never matches word for word. When the two texts agree once
// spaces and slashes are left out, the range is mapped by counting only the
// characters they share.
const SEPARATOR = /[\s/]/;
function kept(text) {
  const at = [];
  for (let i = 0; i < text.length; i++) if (!SEPARATOR.test(text[i])) at.push(i);
  return at;
}
function mapBySkeleton(fromText, toText, start, end) {
  const a = kept(fromText), b = kept(toText);
  if (a.length !== b.length || a.some((i, k) => fromText[i] !== toText[b[k]])) return null;
  const s = a.findIndex(i => i >= start);
  const e = a.findIndex(i => i >= end);
  const last = (e === -1 ? a.length : e) - 1;
  if (s === -1 || last < s) return null;
  return [b[s], b[last] + 1];
}

// The text parts of a verse to draw, and the plain text they spell. Marked
// text keeps its markers as their own parts; they take no offsets.
export function verseParts(plain, marked) {
  if (!marked) return { parts: plain ? [{ kind: 'text', text: plain }] : [], display: plain || '' };
  const parts = parseMarkerParts(marked);
  return { parts, display: parts.filter(p => p.kind === 'text').map(p => p.text).join('') };
}

// Trims a selected range to the words inside it: a drag that starts or ends on
// the space between verses should not mark that space.
export function trimRange(text, start, end) {
  let s = Math.max(0, start), e = Math.min(text.length, end);
  while (s < e && /\s/.test(text[s])) s++;
  while (e > s && /\s/.test(text[e - 1])) e--;
  return [s, e];
}

// One selection → one range per verse it touches. `hits` are the verses in
// reading order, each { book, chapter, verse, lang, text, start, end } with
// start/end already in plain-text offsets (null meaning the verse's own start
// or end). Empty and whitespace-only pieces drop out; the rest share a group
// when they become marks.
export function splitSelection(hits) {
  const out = [];
  const seen = new Set();
  for (const h of hits) {
    const key = verseKey(h.book, h.chapter, h.verse, h.lang);
    if (seen.has(key) || !h.text) continue;
    const [s, e] = trimRange(h.text, h.start ?? 0, h.end ?? h.text.length);
    if (e <= s) continue;
    seen.add(key);
    out.push({ book: h.book, chapter: h.chapter, verse: h.verse, lang: h.lang,
               start: s, end: e, quote: quoteOf(h.text, s, e), text: h.text.slice(s, e) });
  }
  return out;
}

// ── Drawing overlapping marks ────────────────────────────────────────────────
// `parts` come from verseParts; `marks` are { rec, start, end } in display-text
// offsets, oldest first; `noteEnds` is the set of record ids that carry their
// group's note icon. Returns a flat list of:
//   { kind: 'text', text, marks }   a run covered by exactly `marks`
//   { kind: 'marker', marker, marks } a ref marker, inside whatever covers it
//   { kind: 'note', rec }           the note icon, right after rec's last letter
export function buildRenderItems(parts, marks, noteEnds = new Set()) {
  const live = marks.filter(m => m.end > m.start);
  const cuts = new Set();
  live.forEach(m => { cuts.add(m.start); cuts.add(m.end); });
  const covering = (a, b) => live.filter(m => m.start < b && m.end > a).map(m => m.rec);
  const notesAt = pos => live.filter(m => m.end === pos && noteEnds.has(m.rec.id))
    .map(m => ({ kind: 'note', rec: m.rec }));

  const across = at => live.filter(m => m.start < at && m.end > at).map(m => m.rec);

  const items = [];
  let pos = 0;
  for (const part of parts) {
    if (part.kind === 'marker') {
      // A marker sits between two letters; it belongs to a mark that runs on
      // both sides of it, so the highlight does not break around it.
      items.push({ kind: 'marker', marker: part.marker, marks: across(pos) });
      continue;
    }
    const from = pos, to = pos + part.text.length;
    const inside = [...cuts].filter(c => c > from && c < to).sort((a, b) => a - b);
    const points = [from, ...inside, to];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i], b = points[i + 1];
      items.push({ kind: 'text', text: part.text.slice(a - from, b - from), marks: covering(a, b) });
      items.push(...notesAt(b));
    }
    pos = to;
  }
  return items;
}

// How a run covered by several marks looks: the newest highlight colours the
// background, the newest underline or wavy line is drawn under it, and the
// newest mark of all is the one a tap opens. A note-only mark ("none") gets a
// faint dotted line so it can be found and tapped.
export function runStyle(marks) {
  let highlight = null, line = null, dotted = null;
  for (const m of marks) {
    if (m.style === 'highlight') highlight = m.color;
    else if (m.style === 'underline' || m.style === 'wavy') line = { style: m.style, color: m.color };
    else dotted = { style: 'dotted', color: m.color };
  }
  return { highlight, line: line || dotted, top: marks.length ? marks[marks.length - 1] : null };
}

// ── Copying ──────────────────────────────────────────────────────────────────
const bookLabel = (abbr, lang) => BOOK_NAMES[abbr]?.[lang] || BOOK_NAMES[abbr]?.en || abbr;

// "Colossians 3:12–13", "Colossians 3:12–4:2", or both books across a break.
export function formatReference(ranges, lang) {
  if (!ranges.length) return '';
  const sorted = [...ranges].sort(compareVerses);
  const a = sorted[0], b = sorted[sorted.length - 1];
  const name = bookLabel(a.book, lang);
  if (a.book !== b.book) {
    return `${name} ${a.chapter}:${a.verse} – ${bookLabel(b.book, lang)} ${b.chapter}:${b.verse}`;
  }
  if (a.chapter !== b.chapter) return `${name} ${a.chapter}:${a.verse}–${b.chapter}:${b.verse}`;
  if (a.verse !== b.verse) return `${name} ${a.chapter}:${a.verse}–${b.verse}`;
  return `${name} ${a.chapter}:${a.verse}`;
}

// The words, then the reference on a line of its own. Chinese runs verses
// together without spaces; the other languages need one between them.
export function copyText(ranges, uiLang) {
  const sorted = [...ranges].sort(compareVerses);
  const zh = sorted.length && (sorted[0].lang === 'zh' || sorted[0].lang === 'sc');
  const body = sorted.map(r => r.text ?? r.quote).join(zh ? '' : ' ');
  return `${body}\n${formatReference(sorted, uiLang)}`;
}
